/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, November 13th 2021, 11:43:37 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { useControlledInput } from "ui-widgets/forms";
import { TransferList, TransferListProps } from "ui-widgets/select/TransferList";
import {
  APIFormInputErrorWrapper,
  CustomInputValidationErrorWrapper,
  HelperTextInputWrapper,
} from "ui-widgets/forms/FormInputWrappers";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { useAPIFetchList } from "lib/api";
import { PrimaryKeyValue } from "lib/types";
import { differenceBy } from "lodash";
import { GenericAPIMany2ManyFormInputProps } from "ui-widgets/forms/types";

type GenericAPIMany2ManyTransferListFormInputProps<PrimaryObject, RelatedObject> = GenericAPIMany2ManyFormInputProps<
  PrimaryObject,
  RelatedObject
> &
  Pick<TransferListProps, "listHeight" | "listWidth"> & {
    getSearchableName?: (option: RelatedObject) => string;
  };

export type ConcreteAPIMany2ManyTransferListFormInputProps<PrimaryObject, RelatedObject> = Omit<
  GenericAPIMany2ManyTransferListFormInputProps<PrimaryObject, RelatedObject>,
  "apiServicePath" | "apiQueryParams" | "itemIdProperty" | "getOptionLabel" | "getOptionComponent"
>;

export const APIMany2ManyTransferListFormInput = <P, R>(props: GenericAPIMany2ManyTransferListFormInputProps<P, R>) => {
  const {
    field: { onChange, value },
  } = useControlledInput<P>(props);

  const {
    items: apiItemList,
    isMakingAPIRequest,
    isAPIError,
    httpResponseStatusCode,
  } = useAPIFetchList<R>({ ...props, makeRequestOnInitialMount: true }); // Creating fetch list args from props since they are part of APIRelatedObjectSelectProps. This can probably be improved to dynamically destruct all fetchList args only without including irrelvant props as is done here.

  // Default Ids are the value passed on the first render by react hook form. We memoize to avoid tracking the state
  // change of the controlled value
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const field_default_ids: PrimaryKeyValue<R>[] = React.useMemo(() => (value ?? []) as PrimaryKeyValue<R>[], []);

  const [selectedItems, setSelectedItems] = React.useState([]);

  const [unSelectedItems, setUnselectedItems] = React.useState([]);

  const formatItems = (items: R[]) =>
    items.map((v) =>
      Object.create({
        id: v?.[props.itemIdProperty],
        searchableName: props.getSearchableName?.(v),
        renderedItem:
          props.getOptionComponent?.(v, {
            isSelected: selectedItems.some((o) => o?.[props.itemIdProperty] === v?.[props.itemIdProperty]),
          }) ?? props.getOptionLabel?.(v),
      })
    );

  React.useEffect(() => {
    if (!isMakingAPIRequest) {
      setSelectedItems(
        formatItems(
          apiItemList.filter((v) => field_default_ids.indexOf(v?.[props.itemIdProperty] as PrimaryKeyValue<R>) !== -1)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiItemList, isMakingAPIRequest]);

  React.useEffect(() => {
    setUnselectedItems(differenceBy(formatItems(apiItemList), selectedItems, "id")); // Note lodash '_.differenceBy' can handle array of objects whereas '_.difference' function can only handle array of primitive values

    onChange((selectedItems as any).map((v) => v.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  return (
    <APIListProgressWrapper
      showLoader={isMakingAPIRequest}
      isAPIError={isAPIError}
      items={apiItemList}
      httpResponseStatusCode={httpResponseStatusCode}
      {...props}
    >
      <APIFormInputErrorWrapper {...props}>
        <CustomInputValidationErrorWrapper {...props}>
          <HelperTextInputWrapper helperText={props.helperText}>
            <FormControl component="fieldset">
              <FormLabel component="h4">{props.label}</FormLabel>
              <TransferList
                selectedItems={selectedItems}
                SetSelectedItems={setSelectedItems}
                unSelectedItems={unSelectedItems}
                SetUnSelectedItems={setUnselectedItems}
                listWidth={props.listWidth ?? 300}
                listHeight={props.listHeight ?? 250}
                showSearch={Boolean(props.getSearchableName)}
              />
            </FormControl>
          </HelperTextInputWrapper>
        </CustomInputValidationErrorWrapper>
      </APIFormInputErrorWrapper>
    </APIListProgressWrapper>
  );
};
